import { Field, Input, SimpleGrid } from '@chakra-ui/react'
import { SheetSelect, type SheetSelectOption } from '../shared/SheetSelect'
import { useAvailableDistricts } from '../../hooks/useAvailableDistricts'
import type { DistrictOptionsBlock } from '../../types/config'

export interface DistrictFieldsValue {
  locCongressional?: string
  locStateSenate?: string
  locStateHouse?: string
}

export interface DistrictFieldsProps {
  state?: string
  value: DistrictFieldsValue
  onChange: (field: keyof DistrictFieldsValue, next: string) => void
  readOnly?: boolean
}

const ROWS: { field: keyof DistrictFieldsValue; label: string; key: 'congressional' | 'stateSenate' | 'stateHouse' }[] = [
  { field: 'locCongressional', label: 'Congressional district', key: 'congressional' },
  { field: 'locStateSenate', label: 'State senate district', key: 'stateSenate' },
  { field: 'locStateHouse', label: 'State house district', key: 'stateHouse' },
]

/**
 * Three district fields, each a pick-only select when the server can name the options for
 * this state + chamber, and a free-text input otherwise (the server normalizes either way).
 * Pick-only where options exist is deliberate: a typo cannot mint a value that no grant
 * matches, which is the whole failure mode this build closes.
 */
export function DistrictFields({ state, value, onChange, readOnly }: DistrictFieldsProps) {
  const { data } = useAvailableDistricts(state, undefined, true)

  const toOptions = (block?: DistrictOptionsBlock): SheetSelectOption[] =>
    (block?.options ?? []).map((o) => ({ value: o.value, label: o.label }))

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
      {ROWS.map(({ field, label, key }) => {
        const block = data?.options?.[key]
        const options = toOptions(block)
        const pickable = !!block && block.source !== 'None' && options.length > 0
        return (
          <Field.Root key={field}>
            <Field.Label>{label}</Field.Label>
            {pickable ? (
              <SheetSelect
                aria-label={label}
                value={value[field] ?? ''}
                options={options}
                searchable
                placeholder="Select…"
                onChange={(next) => onChange(field, next)}
              />
            ) : (
              <Input
                aria-label={label}
                readOnly={readOnly}
                value={value[field] ?? ''}
                onChange={(e) => onChange(field, e.currentTarget.value)}
              />
            )}
          </Field.Root>
        )
      })}
    </SimpleGrid>
  )
}
